import { useState, useEffect } from "react";
import axios from "axios";
import { Cookie, Shield, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

import { getApiUrl } from '../config/appConfig';
const getAPI = () => getApiUrl();
// getAPI() URL is now auto-detected

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkConsent();
  }, []);

  const checkConsent = async () => {
    try {
      // First check localStorage for quick check
      const localConsent = localStorage.getItem("cookie_consent");
      if (localConsent) {
        setShowBanner(false);
        setLoading(false);
        return;
      }

      // Then check with server (with timeout)
      const res = await axios.get(`${getAPI()}/consent/status`, { 
        withCredentials: true,
        timeout: 5000 // 5 second timeout
      });
      if (res.data.has_consent) {
        localStorage.setItem("cookie_consent", res.data.consent.consent_type);
        setShowBanner(false);
      } else {
        setShowBanner(true);
      }
    } catch (error) {
      // If getAPI() fails, check localStorage only
      const localConsent = localStorage.getItem("cookie_consent");
      if (!localConsent) {
        setShowBanner(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (type) => {
    try {
      const data = {
        essential: true,
        analytics: type === "all",
        marketing: type === "all",
        preferences: type === "all",
        consent_type: type
      };

      await axios.post(`${getAPI()}/consent/accept`, data, { 
        withCredentials: true,
        timeout: 5000
      });
      localStorage.setItem("cookie_consent", type);
      setShowBanner(false);
    } catch (error) {
      // Even if getAPI() fails, set localStorage and hide banner
      localStorage.setItem("cookie_consent", type);
      setShowBanner(false);
    }
  };

  if (loading || !showBanner) return null;

  return (
    <>
      {/* Semi-transparent overlay - allows clicking through on edges */}
      <div 
        className="fixed inset-0 bg-black/40 z-[9998]" 
        onClick={() => handleAccept("necessary_only")}
      />
      
      {/* Cookie consent banner */}
      <div className="fixed inset-x-0 bottom-0 z-[9999] p-4 sm:p-6">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="p-6">
            {/* Header */}
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-violet-100 rounded-full">
                <Cookie className="w-6 h-6 text-violet-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Cookie Settings</h2>
                <p className="text-sm text-gray-500 mt-1">
                  We use cookies to enhance your browsing experience and provide personalized services.
                </p>
              </div>
            </div>

            {/* Cookie types */}
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <Shield className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Essential Cookies</p>
                  <p className="text-xs text-gray-500">Required for the website to function. Always enabled.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Analytics & Marketing</p>
                  <p className="text-xs text-gray-500">Help us improve by allowing analytics and marketing cookies.</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => handleAccept("necessary_only")}
                variant="outline"
                className="flex-1 border-gray-300"
              >
                Accept Necessary Only
              </Button>
              <Button
                onClick={() => handleAccept("all")}
                className="flex-1 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
              >
                Accept All Cookies
              </Button>
            </div>

            {/* Privacy link */}
            <p className="text-xs text-gray-400 text-center mt-4">
              By continuing, you agree to our{" "}
              <a href="/privacy" className="text-violet-600 hover:underline">Privacy Policy</a>
              {" "}and{" "}
              <a href="/terms" className="text-violet-600 hover:underline">Terms of Service</a>.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
